import { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../../api'
import { ProjectContext } from '../../contexts'
import { Resource } from '../../types'
import { Button, DataTable, Heading } from '../../ui'
import Modal from '../../ui/Modal'
import FormWrapper from '../../ui/form/FormWrapper'
import TextInput from '../../ui/form/TextInput'
import { PlusIcon } from '../../ui/icons'
import SourceEditor from '../../ui/SourceEditor'
import ResourceFontModal from '../campaign/editor/ResourceFontModal'

interface LayoutFormData {
    name: string
    html: string
}

export default function Resources() {

    const { t } = useTranslation()
    const [project] = useContext(ProjectContext)
    const [resources, setResources] = useState<Resource[]>([])
    const [showFontCreate, setShowFontCreate] = useState(false)
    const [editingFont, setEditingFont] = useState<Resource | undefined>()
    const [editingLayout, setEditingLayout] = useState<Resource | undefined>()
    const [creatingLayout, setCreatingLayout] = useState(false)

    const loadResources = () => {
        api.resources.all(project.id)
            .then(setResources)
            .catch(() => setResources([]))
    }

    useEffect(() => { loadResources() }, [project.id])

    const fonts = resources.filter(r => r.type === 'font')
    const layouts = resources.filter(r => r.type === 'layout')

    const handleDeleteResource = async (id: number) => {
        await api.resources.delete(project.id, id)
        setResources(resources.filter(r => r.id !== id))
    }

    const handleAddFont = (resource: Resource) => {
        setShowFontCreate(false)
        setResources([...resources, resource])
    }

    const handleUpdateFont = (resource: Resource) => {
        setEditingFont(undefined)
        setResources(resources.map(r => r.id === resource.id ? resource : r))
    }

    const handleCreateLayout = async ({ name, html }: LayoutFormData) => {
        const resource = await api.resources.create(project.id, {
            type: 'layout',
            name,
            value: { html },
        })
        setResources([...resources, resource])
        setCreatingLayout(false)
    }

    const handleUpdateLayout = async ({ name, html }: LayoutFormData) => {
        if (!editingLayout) return
        const updated = await api.resources.update(project.id, editingLayout.id, {
            name,
            value: { html },
        })
        setResources(resources.map(r => r.id === editingLayout.id ? updated : r))
        setEditingLayout(undefined)
    }

    return (
        <>
            <Heading title={t('fonts')} size="h3" actions={
                <Button
                    size="small"
                    variant="primary"
                    onClick={() => setShowFontCreate(true)}
                    icon={<PlusIcon />}
                >{t('add_font')}</Button>
            } />
            <DataTable
                items={fonts}
                itemKey={({ item }) => item.id}
                onSelectRow={item => setEditingFont(item)}
                columns={[
                    { key: 'name', title: t('name') },
                    {
                        key: 'family',
                        title: t('font_family'),
                        cell: ({ item }) => item.value.value,
                    },
                    {
                        key: 'url',
                        title: t('url'),
                        cell: ({ item }) => item.value.url,
                    },
                    {
                        key: 'weights',
                        title: t('font_weights'),
                        cell: ({ item }) => (item.value.weights ?? []).join(', ') || '—',
                    },
                    {
                        key: 'options',
                        title: t('options'),
                        align: 'end',
                        cell: ({ item }) => (
                            <Button
                                size="small"
                                variant="destructive"
                                onClick={async (e) => {
                                    e.stopPropagation()
                                    await handleDeleteResource(item.id)
                                }}>
                                {t('delete')}
                            </Button>
                        ),
                    },
                ]}
            />

            <Heading title={t('layouts')} size="h3" actions={
                <Button
                    size="small"
                    variant="primary"
                    onClick={() => setCreatingLayout(true)}
                    icon={<PlusIcon />}
                >{t('add_layout')}</Button>
            } />
            <DataTable
                items={layouts}
                itemKey={({ item }) => item.id}
                onSelectRow={item => setEditingLayout(item)}
                columns={[
                    { key: 'name', title: t('name') },
                    {
                        key: 'options',
                        title: t('options'),
                        align: 'end',
                        cell: ({ item }) => (
                            <Button
                                size="small"
                                variant="destructive"
                                onClick={async () => await handleDeleteResource(item.id)}>
                                {t('delete')}
                            </Button>
                        ),
                    },
                ]}
            />

            <ResourceFontModal
                open={showFontCreate}
                onClose={() => setShowFontCreate(false)}
                onInsert={handleAddFont}
            />

            <ResourceFontModal
                open={!!editingFont}
                resource={editingFont}
                onClose={() => setEditingFont(undefined)}
                onInsert={handleUpdateFont}
            />

            <Modal
                title={t('create_layout')}
                open={creatingLayout}
                onClose={() => setCreatingLayout(false)}
                size="large"
            >
                <FormWrapper<LayoutFormData>
                    onSubmit={handleCreateLayout}
                    submitLabel={t('create')}
                >
                    {form => <>
                        <TextInput.Field
                            form={form}
                            name="name"
                            label={t('name')}
                            required />
                        <div style={{ height: 400 }}>
                            <SourceEditor
                                height="100%"
                                defaultLanguage="html"
                                defaultValue=""
                                onChange={(value) => form.setValue('html', value ?? '')}
                            />
                        </div>
                    </>}
                </FormWrapper>
            </Modal>

            <Modal
                title={t('edit_layout')}
                open={!!editingLayout}
                onClose={() => setEditingLayout(undefined)}
                size="large"
            >
                {editingLayout && (
                    <FormWrapper<LayoutFormData>
                        onSubmit={handleUpdateLayout}
                        submitLabel={t('save')}
                        defaultValues={{
                            name: editingLayout.name,
                            html: editingLayout.value?.html ?? '',
                        }}
                    >
                        {form => <>
                            <TextInput.Field
                                form={form}
                                name="name"
                                label={t('name')}
                                required />
                            <div style={{ height: 400 }}>
                                <SourceEditor
                                    key={editingLayout.id}
                                    height="100%"
                                    defaultLanguage="html"
                                    defaultValue={editingLayout.value?.html ?? ''}
                                    onChange={(value) => form.setValue('html', value ?? '')}
                                />
                            </div>
                        </>}
                    </FormWrapper>
                )}
            </Modal>
        </>
    )
}
